/**
 * Global API configuration.
 * Used only by TanStack Query fetchers (e.g. fetchClients). No Vite proxy; axios calls go directly to the backend.
 * Backend must enable CORS for the frontend origin.
 *
 * Every value below can be overridden per deployment via `@/config/runtime_config` (container
 * env vars); the literal here is the fallback used when the corresponding env var is unset.
 */

import runtimeConfig from '@/config/runtime_config'

/** Backend base URL. All TanStack API calls are prefixed with this. */
export const apiBaseUrl = runtimeConfig.API_BASE_URL ?? 'http://localhost:8000/api/v0'

/** Page size for clients list (infinite scroll). */
export const clientsPageLimit = Number(runtimeConfig.CLIENTS_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the clients list. */
export const clientsPollingInterval = Number(runtimeConfig.CLIENTS_POLLING_INTERVAL_MS ?? 15000)

/** Page size for payments list (infinite scroll). */
export const paymentsPageLimit = Number(runtimeConfig.PAYMENTS_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the payments list. */
export const paymentsPollingInterval = Number(runtimeConfig.PAYMENTS_POLLING_INTERVAL_MS ?? 15000)

/** Payment card polling interval (ms) while the payment is not identified. */
export const paymentCardUnidentifiedPollingInterval = Number(
  runtimeConfig.PAYMENT_CARD_UNIDENTIFIED_POLLING_INTERVAL_MS ?? 15000,
)

/** Payment card polling interval (ms) once the payment is identified. */
export const paymentCardIdentifiedPollingInterval = Number(
  runtimeConfig.PAYMENT_CARD_IDENTIFIED_POLLING_INTERVAL_MS ?? 300000,
)

/** Page size for service categories list (infinite scroll). */
export const serviceCategoriesPageLimit = Number(runtimeConfig.SERVICE_CATEGORIES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the service categories list. */
export const serviceCategoriesPollingInterval = Number(
  runtimeConfig.SERVICE_CATEGORIES_POLLING_INTERVAL_MS ?? 15000,
)

/** Page size for subscription types list (infinite scroll). */
export const subscriptionTypesPageLimit = Number(runtimeConfig.SUBSCRIPTION_TYPES_PAGE_LIMIT ?? 100)

/** Page size for a person's purchased subscriptions. */
export const subscriptionsPageLimit = Number(runtimeConfig.SUBSCRIPTIONS_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the subscription types list. */
export const subscriptionTypesPollingInterval = Number(
  runtimeConfig.SUBSCRIPTION_TYPES_POLLING_INTERVAL_MS ?? 15000,
)

/** Page size for service types list (infinite scroll). */
export const serviceTypesPageLimit = Number(runtimeConfig.SERVICE_TYPES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the service types list. */
export const serviceTypesPollingInterval = Number(
  runtimeConfig.SERVICE_TYPES_POLLING_INTERVAL_MS ?? 15000,
)

/**
 * Polling interval (ms) used by every query inside the business service type
 * info card. Longer cadence than the list tabs because the card aggregates
 * stats + the full occurrences stream and the underlying data changes at
 * human-action speed (new sessions scheduled), not transaction speed.
 */
export const businessServiceTypeCardPollingInterval = Number(
  runtimeConfig.BUSINESS_SERVICE_TYPE_CARD_POLLING_INTERVAL_MS ?? 60000,
)

/** Page size for the per-type business services stream (`/business-services/types/{id}`). */
export const businessServicesByTypePageLimit = Number(
  runtimeConfig.BUSINESS_SERVICES_BY_TYPE_PAGE_LIMIT ?? 100,
)

/** Page size for business services list (infinite scroll). */
export const businessServicesPageLimit = Number(runtimeConfig.BUSINESS_SERVICES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the business services list. */
export const businessServicesPollingInterval = Number(
  runtimeConfig.BUSINESS_SERVICES_POLLING_INTERVAL_MS ?? 15000,
)

/** Page size for the client-visit-counts-by-period stream (Finances tab drains every page). */
export const clientVisitCountsPageLimit = Number(
  runtimeConfig.CLIENT_VISIT_COUNTS_PAGE_LIMIT ?? 100,
)

/** Page size for the performer-stats-by-period stream (Stuff tab drains every page). */
export const performerStatsPageLimit = Number(runtimeConfig.PERFORMER_STATS_PAGE_LIMIT ?? 100)

/** Page size for performers list (infinite scroll). */
export const performersPageLimit = Number(runtimeConfig.PERFORMERS_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the performers list. */
export const performersPollingInterval = Number(
  runtimeConfig.PERFORMERS_POLLING_INTERVAL_MS ?? 15000,
)

/** Page size for rules list (infinite scroll). */
export const rulesPageLimit = Number(runtimeConfig.RULES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the rules list. */
export const rulesPollingInterval = Number(runtimeConfig.RULES_POLLING_INTERVAL_MS ?? 15000)

/** Page size for diseases list (infinite scroll). */
export const diseasesPageLimit = Number(runtimeConfig.DISEASES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the diseases list. */
export const diseasesPollingInterval = Number(runtimeConfig.DISEASES_POLLING_INTERVAL_MS ?? 15000)

/** Page size for booking types list (fully drained for the Types tab / room sub-tabs). */
export const bookingTypesPageLimit = Number(runtimeConfig.BOOKING_TYPES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the booking types list. */
export const bookingTypesPollingInterval = Number(
  runtimeConfig.BOOKING_TYPES_POLLING_INTERVAL_MS ?? 15000,
)

/** Polling interval (ms) for auto-refreshing the weekly booking schedule grid. */
export const bookingSchedulePollingInterval = Number(
  runtimeConfig.BOOKING_SCHEDULE_POLLING_INTERVAL_MS ?? 15000,
)

/** Page size for the groups list (Clients tab's "Группы" pane). */
export const groupsPageLimit = Number(runtimeConfig.GROUPS_PAGE_LIMIT ?? 100)

/** Polling interval (ms) for auto-refreshing the groups list. */
export const groupsPollingInterval = Number(runtimeConfig.GROUPS_POLLING_INTERVAL_MS ?? 15000)

/* ---------------------------------------------------------------------------
 * Per-client streams used by the client info card (`InfoViewLayout`).
 *
 * Page limits match the global lists. Each stream is paginated DESCENDING by
 * date and aligned to the others by `useAlignedClientStreams`, which trims
 * older items until every stream has caught up. Polling is driven by a
 * single 1-minute interval (matches the on-mount/while-active freshness
 * policy in the plan); the queries opt out of background refetch via
 * `refetchIntervalInBackground: false`. */

/** Page size for the per-client payments stream (`/payments/clients/{id}`). */
export const clientPaymentsPageLimit = Number(runtimeConfig.CLIENT_PAYMENTS_PAGE_LIMIT ?? 100)

/** Page size for the per-client business services stream (`/business-services/clients/{id}`). */
export const clientBusinessServicesPageLimit = Number(
  runtimeConfig.CLIENT_BUSINESS_SERVICES_PAGE_LIMIT ?? 100,
)

/** Page size for the per-client balances stream (`/balances/person/{id}`). */
export const clientBalancesPageLimit = Number(runtimeConfig.CLIENT_BALANCES_PAGE_LIMIT ?? 100)

/** Page size for the per-client diseases stream (`/diseases/clients/{id}`). */
export const clientDiseasesPageLimit = Number(runtimeConfig.CLIENT_DISEASES_PAGE_LIMIT ?? 100)

/** Polling interval (ms) used by every client-info-card query (person + four list streams). */
export const clientCardPollingInterval = Number(
  runtimeConfig.CLIENT_CARD_POLLING_INTERVAL_MS ?? 15000,
)
